import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import * as siteGroupReportsApi from '@sophocles/api/site-group-operational-reports';

// Entity adapter for normalized state management
// Site groups are identified by their site_group_id
const siteGroupReportsAdapter = createEntityAdapter({
  selectId: report => report.site_group_id,
});

// Async thunks for site group operational reports
export const fetchSiteGroupReports = createAsyncThunk(
  'siteGroupOperationalReports/fetchSiteGroupReports',
  async (params = {}, { rejectWithValue }) => {
    try {
      return await siteGroupReportsApi.fetchSiteGroupReports(params);
    } catch (error) {
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to load site group reports';
      return rejectWithValue(message);
    }
  },
);

export const fetchSiteGroupReport = createAsyncThunk(
  'siteGroupOperationalReports/fetchSiteGroupReport',
  async ({ id, params = {} }, { rejectWithValue }) => {
    try {
      return await siteGroupReportsApi.fetchSiteGroupReport(id, params);
    } catch (error) {
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to load site group report';
      return rejectWithValue(message);
    }
  },
);

// Initial state
const initialState = {
  reports: siteGroupReportsAdapter.getInitialState({
    loading: false,
    error: null,
    count: 0,
    next: null,
    previous: null,
  }),
  currentReport: {
    data: null,
    loading: false,
    error: null,
  },
  filters: {
    site_group: null,
    search: '',
  },
  pagination: {
    page: 1,
    limit: 50,
    offset: 0,
  },
  sorting: {
    sortBy: 'site_group_name',
    sortDesc: false,
  },
};

// Slice
const siteGroupOperationalReportsSlice = createSlice({
  name: 'siteGroupOperationalReports',
  initialState,
  reducers: {
    // Filter actions
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1;
      state.pagination.offset = 0;
    },
    resetFilters: state => {
      state.filters = initialState.filters;
      state.pagination.page = 1;
      state.pagination.offset = 0;
    },
    setSiteGroupFilter: (state, action) => {
      state.filters.site_group = action.payload;
      state.pagination.page = 1;
      state.pagination.offset = 0;
    },
    setSearchFilter: (state, action) => {
      state.filters.search = action.payload;
      state.pagination.page = 1;
      state.pagination.offset = 0;
    },

    // Pagination actions
    setPage: (state, action) => {
      state.pagination.page = action.payload;
      state.pagination.offset = (action.payload - 1) * state.pagination.limit;
    },
    setLimit: (state, action) => {
      state.pagination.limit = action.payload;
      state.pagination.page = 1;
      state.pagination.offset = 0;
    },

    // Sorting actions
    setSortBy: (state, action) => {
      state.sorting.sortBy = action.payload;
      state.pagination.page = 1;
      state.pagination.offset = 0;
    },
    setSortDesc: (state, action) => {
      state.sorting.sortDesc = action.payload;
      state.pagination.page = 1;
      state.pagination.offset = 0;
    },

    // Clear current report
    clearCurrentReport: state => {
      state.currentReport.data = null;
      state.currentReport.error = null;
    },
  },
  extraReducers: builder => {
    // Fetch site group reports (list)
    builder.addCase(fetchSiteGroupReports.pending, state => {
      state.reports.loading = true;
      state.reports.error = null;
    });
    builder.addCase(fetchSiteGroupReports.fulfilled, (state, action) => {
      state.reports.loading = false;
      state.reports.count = action.payload.count;
      state.reports.next = action.payload.next;
      state.reports.previous = action.payload.previous;
      siteGroupReportsAdapter.setAll(state.reports, action.payload.results);
    });
    builder.addCase(fetchSiteGroupReports.rejected, (state, action) => {
      state.reports.loading = false;
      state.reports.error = action.payload || action.error.message;
    });

    // Fetch single site group report
    builder.addCase(fetchSiteGroupReport.pending, state => {
      state.currentReport.loading = true;
      state.currentReport.error = null;
    });
    builder.addCase(fetchSiteGroupReport.fulfilled, (state, action) => {
      state.currentReport.loading = false;
      state.currentReport.data = action.payload;
    });
    builder.addCase(fetchSiteGroupReport.rejected, (state, action) => {
      state.currentReport.loading = false;
      state.currentReport.error = action.payload || action.error.message;
    });
  },
});

// Export actions
export const {
  setFilters,
  resetFilters,
  setSiteGroupFilter,
  setSearchFilter,
  setPage,
  setLimit,
  setSortBy,
  setSortDesc,
  clearCurrentReport,
} = siteGroupOperationalReportsSlice.actions;

// Selectors
const { selectAll, selectById, selectIds, selectEntities, selectTotal } = siteGroupReportsAdapter.getSelectors(
  state => state.siteGroupOperationalReports.reports,
);

export const selectAllSiteGroupReports = selectAll;
export const selectSiteGroupReportById = selectById;
export const selectSiteGroupReportIds = selectIds;
export const selectSiteGroupReportEntities = selectEntities;
export const selectTotalSiteGroupReports = selectTotal;

export const selectSiteGroupReportsLoading = state => state.siteGroupOperationalReports.reports.loading;
export const selectSiteGroupReportsError = state => state.siteGroupOperationalReports.reports.error;
export const selectSiteGroupReportsCount = state => state.siteGroupOperationalReports.reports.count;

export const selectCurrentSiteGroupReport = state => state.siteGroupOperationalReports.currentReport.data;
export const selectCurrentSiteGroupReportLoading = state => state.siteGroupOperationalReports.currentReport.loading;
export const selectCurrentSiteGroupReportError = state => state.siteGroupOperationalReports.currentReport.error;

export const selectSiteGroupFilters = state => state.siteGroupOperationalReports.filters;
export const selectSiteGroupPagination = state => state.siteGroupOperationalReports.pagination;
export const selectSiteGroupSorting = state => state.siteGroupOperationalReports.sorting;

export const selectSiteGroupTotalPages = state =>
  Math.ceil(state.siteGroupOperationalReports.reports.count / state.siteGroupOperationalReports.pagination.limit);

// Export reducer
export default siteGroupOperationalReportsSlice.reducer;
